import { BASE_URL, BACKEND_URL } from '../../../configs'
import { alertError, alertSuccess, createJsonHeaders, logoutOnRequestOrResponseJson } from '../../../api'

export interface SupervisionTemplate {
  id: number;
  name: string;
  frequency: string;
  startDate: string;
  dueDate: string;
  isDraft: boolean;
  botId: number;
  userIds: any;
  createdAt: string;
  updatedAt: string;
}

export interface CreateSupervisionTemplatePayload {
  botId: number;
  frequency: string;
  isDraft: boolean;
  name: string;
  userIds: number[];
}

export interface CreateSupervisionTemplateResponse {
  message: string;
  data: SupervisionTemplate;
}

export interface UserOption {
  label: string;
  value: number;
}

export interface UsersListResponse {
  carers_list: UserOption[];
  managers_list: UserOption[];
}

export interface TemplateQueryParams {
  search?: string;
  page?: number;
  size?: number;
  [key: string]: any;
}

export function getSupervisionTemplates(botId: number, queryParams?: TemplateQueryParams) {
  let url = `${BASE_URL}/bot/${botId}/supervision/template/list`

  if (queryParams && Object.keys(queryParams).length > 0) {
    const params = new URLSearchParams()
    Object.entries(queryParams).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, value.toString())
      }
    })

    const queryString = params.toString()
    if (queryString) {
      url = `${url}?${queryString}`
    }
  }

  return fetch(url, {
    credentials: 'include',
    headers: createJsonHeaders(),
    method: 'GET',
  })
    .then(response => logoutOnRequestOrResponseJson(response))
    .then(response => {
      return response
    })
    .catch(error => {
      alertError('Failed to get supervision templates')
      throw error
    })
}

export function createSupervisionTemplate(payload: CreateSupervisionTemplatePayload): Promise<CreateSupervisionTemplateResponse> {
  return fetch(`${BASE_URL}/bot/${payload.botId}/supervision/template`, {
    credentials: 'include',
    headers: createJsonHeaders(),
    method: 'POST',
    body: JSON.stringify(payload),
  })
    .then(response => logoutOnRequestOrResponseJson(response))
    .then(response => {
      alertSuccess(response.message || 'Supervision template created successfully', 3000)
      return response
    })
    .catch(error => {
      alertError(error.message || 'Failed to create supervision template')
      throw error
    })
}

export function getUsersList(botId: number): Promise<UsersListResponse> {
  return fetch(`${BACKEND_URL}/users/management/bot/${botId}/carers-and-managers`, {
    credentials: 'include',
    headers: createJsonHeaders(),
    method: 'GET',
  })
    .then(response => logoutOnRequestOrResponseJson(response))
    .then(response => {
      return response
    })
    .catch(error => {
      alertError('Failed to fetch users list')
      throw error
    })
}

export interface DeleteSupervisionTemplateResponse {
  message: string;
  data: null;
}

export function deleteSupervisionTemplate(botId: number, templateId: number): Promise<DeleteSupervisionTemplateResponse> {
  return fetch(`${BASE_URL}/bot/${botId}/supervision/template/${templateId}`, {
    credentials: 'include',
    headers: createJsonHeaders(),
    method: 'DELETE',
  })
    .then(response => logoutOnRequestOrResponseJson(response))
    .then(response => {
      alertSuccess(response.message || 'Supervision template deleted successfully', 3000)
      return response
    })
    .catch(error => {
      alertError('Failed to delete supervision template')
      throw error
    })
}

export interface SupervisionTemplateData {
  id: number
  name: string
  frequency: string
  startDate: string
  dueDate: string
  isDraft: boolean
  botId: number
  userIds: number[] | null
  createdAt: string
  updatedAt: string
}

export interface GetSupervisionTemplateResponse {
  message: string
  data: SupervisionTemplateData
}

export function getSupervisionTemplate(botId: number, templateId: number): Promise<GetSupervisionTemplateResponse> {
  return fetch(`${BASE_URL}/bot/${botId}/supervision/template/${templateId}`, {
    credentials: 'include',
    headers: createJsonHeaders(),
    method: 'GET',
  })
    .then(response => logoutOnRequestOrResponseJson(response))
    .then(response => {
      return response
    })
    .catch(error => {
      alertError('Failed to fetch supervision template')
      throw error
    })
}

export interface UpdateSupervisionTemplatePayload {
  botId: number
  name: string
  frequency: string
  startDate: string
  dueDate: string
  isDraft: boolean
  userIds: number[]
}

export interface UpdateSupervisionTemplateResponse {
  message: string
  data: SupervisionTemplateData
}

export function updateSupervisionTemplate(botId: number, templateId: number, payload: UpdateSupervisionTemplatePayload): Promise<UpdateSupervisionTemplateResponse> {
  return fetch(`${BASE_URL}/bot/${botId}/supervision/template/${templateId}`, {
    credentials: 'include',
    headers: createJsonHeaders(),
    method: 'PUT',
    body: JSON.stringify(payload),
  })
    .then(response => logoutOnRequestOrResponseJson(response))
    .then(response => {
      alertSuccess(response.message || 'Supervision template updated successfully', 3000)
      return response
    })
    .catch(error => {
      alertError(error.message || 'Failed to update supervision template')
      throw error
    })
}

export interface SectionOption {
  id: number;
  name: string;
  code: string;
  defaultFlag: boolean;
  questions: null | any[];
}

export interface GetSectionOptionsResponse {
  message: string;
  data: SectionOption[];
}

export function getSectionOptions(botId: number): Promise<GetSectionOptionsResponse> {
  return fetch(`${BASE_URL}/bot/${botId}/supervision/template/sections`, {
    credentials: 'include',
    headers: createJsonHeaders(),
    method: 'GET',
  })
    .then(response => logoutOnRequestOrResponseJson(response))
    .then(response => {
      return response
    })
    .catch(error => {
      alertError('Failed to fetch section options')
      throw error
    })
}

export interface QuestionOption {
  isCorrect: boolean;
  text: string;
}

export interface TemplateQuestion {
  questionText: string;
  minAnswers: number;
  maxAnswers: number;
  requiresAnswer: boolean;
  sectionId: number;
  sequenceNo: number;
  options?: QuestionOption[];
}

export interface CreateTemplateQuestionsPayload {
  questions: TemplateQuestion[];
}

export interface CreateTemplateQuestionsResponse {
  message: string;
  data: number[];
}

export function createTemplateQuestions(
  botId: number,
  templateId: number,
  payload: CreateTemplateQuestionsPayload
): Promise<CreateTemplateQuestionsResponse> {
  return fetch(`${BASE_URL}/bot/${botId}/supervision/template/${templateId}/questions`, {
    credentials: 'include',
    headers: createJsonHeaders(),
    method: 'POST',
    body: JSON.stringify(payload),
  })
    .then(response => logoutOnRequestOrResponseJson(response))
    .then(response => {
      alertSuccess(response.message || 'Questions saved successfully', 3000)
      return response
    })
    .catch(error => {
      alertError(error.message || 'Failed to save template questions')
      throw error
    })
}

export interface TemplateQuestionData {
  questionId: number;
  sectionId: number;
  questionText: string;
  minAnswers: number;
  maxAnswers: number;
  aiGoal: string | null;
  requiresAnswer: boolean;
  options: any[];
  nextQuestionId: number | null;
  sequenceNo: number | null;
}

export interface GetTemplateQuestionsResponse {
  message: string;
  data: TemplateQuestionData[];
}

export function getTemplateQuestions(
  botId: number,
  templateId: number
): Promise<GetTemplateQuestionsResponse> {
  return fetch(`${BASE_URL}/bot/${botId}/supervision/template/${templateId}/questions`, {
    credentials: 'include',
    headers: createJsonHeaders(),
    method: 'GET',
  })
    .then(response => logoutOnRequestOrResponseJson(response))
    .then(response => {
      return response
    })
    .catch(error => {
      alertError('Failed to fetch template questions')
      throw error
    })
}

export interface UpdateTemplateQuestionsBatchPayload {
  questions: {
    questionId?: number;
    sectionId: number;
    questionText: string;
    minAnswers: number;
    maxAnswers: number;
    requiresAnswer: boolean;
    options?: {
      id?: number;
      isCorrect: boolean;
      text: string;
    }[];
    nextQuestionId?: number;
    sequenceNo?: number;
    aiGoal?: string | null;
  }[];
}

export interface UpdateTemplateQuestionsBatchResponse {
  message: string;
  data: number[];
}

export interface DuplicateSupervisionTemplatePayload {
  name: string;
  frequency: string;
  startDate: string;
  dueDate: string;
  isDraft: boolean;
}

export interface DuplicateSupervisionTemplateResponse {
  message: string;
  data: SupervisionTemplateData;
}

/**
 * Updates multiple questions for a supervision template in a single batch operation
 * @param botId - The ID of the bot
 * @param templateId - The ID of the supervision template
 * @param payload - The questions data to update
 * @returns Promise with the response containing updated question IDs
 */
export function updateTemplateQuestionsBatch(
  botId: number,
  templateId: number,
  payload: UpdateTemplateQuestionsBatchPayload
): Promise<UpdateTemplateQuestionsBatchResponse> {
  return fetch(`${BASE_URL}/bot/${botId}/supervision/template/${templateId}/questions/batch`, {
    credentials: 'include',
    headers: createJsonHeaders(),
    method: 'PUT',
    body: JSON.stringify(payload),
  })
    .then(response => logoutOnRequestOrResponseJson(response))
    .then(response => {
      alertSuccess(response.message || 'Questions updated successfully', 3000)
      return response
    })
    .catch(error => {
      alertError(error.message || 'Failed to update template questions')
      throw error
    })
}

/**
 * Duplicates an existing supervision template
 * @param botId - The ID of the bot
 * @param templateId - The ID of the supervision template to duplicate
 * @param payload - The data for the duplicated template
 * @returns Promise with the response containing the new template data
 */
export function duplicateSupervisionTemplate(
  botId: number,
  templateId: number,
  payload: DuplicateSupervisionTemplatePayload
): Promise<DuplicateSupervisionTemplateResponse> {
  return fetch(`${BASE_URL}/bot/${botId}/supervision/template/${templateId}/duplicate`, {
    credentials: 'include',
    headers: createJsonHeaders(),
    method: 'POST',
    body: JSON.stringify(payload),
  })
    .then(response => logoutOnRequestOrResponseJson(response))
    .then(response => {
      alertSuccess(response.message || 'Supervision template duplicated successfully', 3000)
      return response
    })
    .catch(error => {
      alertError(error.message || 'Failed to duplicate supervision template')
      throw error
    })
}
